import { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { CheckCircle, XCircle, Loader2, ArrowRight, RefreshCw, Home } from 'lucide-react';
import axios from 'axios';
import { API } from '@/App';
import SiteHeader from '@/components/SiteHeader';
import Footer from '@/components/Footer';

const PaymentConfirmationPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('loading');
  const [checkout, setCheckout] = useState(null);
  const [pollCount, setPollCount] = useState(0);

  const checkoutId = searchParams.get('checkout_id');
  const hasError = searchParams.get('error') === 'true';

  useEffect(() => {
    if (!checkoutId) {
      setStatus('error');
      return;
    }
    if (hasError) {
      setStatus('error');
      return;
    }
    verifyPayment();
  }, [checkoutId]);

  const verifyPayment = async () => {
    if (!checkoutId) return;
    setStatus('loading');
    try {
      const resp = await axios.get(`${API}/helloasso/checkout/${checkoutId}/verify`);
      const data = resp.data;
      if (data.status === 'completed') {
        setStatus('success');
        setCheckout(data.checkout);
        const updatedUser = await axios.get(`${API}/auth/me`);
        localStorage.setItem('user', JSON.stringify(updatedUser.data));
      } else {
        setPollCount(prev => prev + 1);
        if (pollCount < 3) {
          setTimeout(verifyPayment, 3000);
        } else {
          setStatus('pending');
          setCheckout(data.checkout);
        }
      }
    } catch {
      setStatus('error');
    }
  };

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Outfit', 'Roboto', sans-serif" }} data-testid="payment-confirmation-page">
      <SiteHeader />

      <div className="relative overflow-hidden" style={{ background: 'linear-gradient(135deg, #1e3a5f 0%, #2d5f8a 50%, #35AFE4 100%)' }}>
        <div className="max-w-3xl mx-auto px-4 py-16 sm:py-20 text-center">
          <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4 tracking-tight">
            Confirmation de paiement
          </h1>
        </div>
        <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="block w-full h-[40px]">
          <path fill="#f8f9fb" d="M0,60 L0,20 Q360,60 720,30 T1440,20 L1440,60 Z" />
        </svg>
      </div>

      <div className="bg-[#f8f9fb] py-12 sm:py-16 px-4">
        <div className="max-w-lg mx-auto">

          {status === 'loading' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-10 text-center animate-fadeIn" data-testid="payment-loading">
              <Loader2 size={48} className="animate-spin text-[#35AFE4] mx-auto mb-6" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Verification du paiement...</h2>
              <p className="text-gray-500 text-sm">Nous verifions votre paiement aupres de HelloAsso</p>
            </div>
          )}

          {status === 'success' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-fadeIn" data-testid="payment-success">
              <div className="bg-emerald-500 p-8 text-center">
                <CheckCircle size={56} className="text-white mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white">Paiement réussi !</h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600 text-center">
                  Votre adhésion à la SFOALC est maintenant active. Un reçu fiscal vous sera envoyé par email.
                </p>
                {checkout && (
                  <div className="bg-gray-50 rounded-xl p-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Type</span>
                      <span className="font-semibold text-gray-900">
                        {checkout.subscription_type === 'ophthalmologist' ? 'Praticien' : 'Interne'}
                      </span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-gray-500">Montant</span>
                      <span className="font-semibold text-gray-900">{checkout.amount} EUR</span>
                    </div>
                  </div>
                )}
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full py-3 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
                  data-testid="btn-go-dashboard"
                >
                  Accéder à mon espace <ArrowRight size={16} />
                </button>
              </div>
            </div>
          )}

          {status === 'pending' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-fadeIn" data-testid="payment-pending">
              <div className="bg-amber-500 p-8 text-center">
                <RefreshCw size={48} className="text-white mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white">Paiement en attente</h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600 text-center">
                  Votre paiement est en cours de traitement. Cela peut prendre quelques minutes.
                </p>
                <button
                  onClick={() => { setPollCount(0); verifyPayment(); }}
                  className="w-full py-3 rounded-xl bg-amber-500 hover:bg-amber-600 text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
                  data-testid="btn-retry-verify"
                >
                  <RefreshCw size={16} /> Vérifier a nouveau
                </button>
                <button
                  onClick={() => navigate('/dashboard')}
                  className="w-full py-3 rounded-xl border border-gray-200 text-gray-600 font-semibold text-sm hover:bg-gray-50 transition-all flex items-center justify-center gap-2"
                  data-testid="btn-go-dashboard-pending"
                >
                  <Home size={16} /> Retour au tableau de bord
                </button>
              </div>
            </div>
          )}

          {status === 'error' && (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden animate-fadeIn" data-testid="payment-error">
              <div className="bg-red-500 p-8 text-center">
                <XCircle size={56} className="text-white mx-auto mb-4" />
                <h2 className="text-2xl font-bold text-white">Paiement échoué</h2>
              </div>
              <div className="p-6 space-y-4">
                <p className="text-gray-600 text-center">
                  {hasError
                    ? "Le paiement a été annule ou a rencontre une erreur."
                    : "Impossible de vérifier le paiement. Veuillez reessayer."}
                </p>
                <button
                  onClick={() => navigate('/adhesion')}
                  className="w-full py-3 rounded-xl bg-[#1e3a5f] hover:bg-[#162d4a] text-white font-bold text-sm transition-all flex items-center justify-center gap-2"
                  data-testid="btn-retry-payment"
                >
                  Reessayer l'adhesion
                </button>
              </div>
            </div>
          )}

        </div>
      </div>

      <Footer />

      <style>{`
        .animate-fadeIn { animation: fadeIn 0.4s ease-out; }
        @keyframes fadeIn { from { opacity: 0; transform: translateY(12px); } to { opacity: 1; transform: translateY(0); } }
      `}</style>
    </div>
  );
};

export default PaymentConfirmationPage;
